import type {ClipEvents, ClipProps, ClipState} from "../clip";
import {EventEmitterMixin} from "../../mixins";
import {Serializer} from "../../services";
import {serializable} from "../../services";
import {Timestamp} from "../../models";
import type {ClipType} from "./clip.types";
import {MediaSourceRef} from "../../sources/media-source";
import {Container} from "pixi.js";
import {Track} from "../../tracks";
import type {float, frame} from "../../types";
import {replaceKeyframes} from "./clip.utils";
import { ValidationError } from '../../errors';
import {AudioMixer} from "../../media/audio-mixer";

export class Clip<Props extends ClipProps = ClipProps> extends EventEmitterMixin<ClipEvents, typeof Serializer>(Serializer) {

    @serializable()
    public _name: undefined | string;

    @serializable(Timestamp)
    public _start: Timestamp = new Timestamp();

    @serializable(Timestamp)
    public _stop: Timestamp = new Timestamp();

    public _hasAudioStream: boolean = false;

    public get hasAudioStream(): boolean {
        return this._hasAudioStream;
    }

    public set hasAudioStream(value: boolean) {
        this._hasAudioStream = value;
    }

    public readonly type: ClipType = 'base';

    // Todo: Generalize for all sources beyond audio/video
    public source?: MediaSourceRef;

    public props?: Props;

    public readonly view = new Container();

    public readonly createdAt: Date = new Date();

    public disabled = false;

    public state: ClipState = 'IDLE';

    // Track (aka Layer) the clip is on.
    public track?: Track<Clip>;

    public get name(): string | undefined {
        return this._name;
    }

    public set name(name: string) {
        this._name = name;
    }

    public get start(): Timestamp {
        return this._start;
    }

    public get stop(): Timestamp {
        return this._stop;
    }

    public constructor(props: ClipProps = {}) {
        super();
        Object.assign(this, props);
    }

    public async connect(track: Track<Clip>): Promise<void> {
        this.state = 'ATTACHED';
        this.track = track;
        this.trigger('attach', undefined);
    }

    public set start(time: frame | Timestamp) {
        if (typeof time == 'number') {
            this.start.frames = time;
        } else {
            this._start = time;
        }

        if (this.start.millis >= this.stop.millis) {
            // make stop one frame larger than the start
            this.stop.millis = this.start.millis + 1;
        }

        this.trigger('frame', this.start.frames);
    }

    public CompositionTimeToClipTime(compSec: number) {
        return compSec;
    }

    public get timelineDuration(): Timestamp {
        return this.stop.subtract(this.start);
    }

    public offsetBy(time: frame | Timestamp): this {
        if (typeof time == 'number') {
            this.start.addFrames(time);
            this.stop.addFrames(time);
            this.trigger('offsetBy', Timestamp.fromFrames(time));
        } else {
            this.start.addMillis(time.millis);
            this.stop.addMillis(time.millis);
            this.trigger('offsetBy', time);
        }

        this.trigger('frame', undefined);

        return this;
    }

    /**
     * Triggered when the clip is
     * added to the composition
     */
    public async init(): Promise<void> { }

    /**
     * Triggered when the clip enters the scene
     */
    public enter(): void { }

    /**
     * Triggered for each redraw of the scene.
     * Can return a promise which will be awaited
     * during export.
     * @param time the current time to render
     */
    public update(time: Timestamp): void | Promise<void> {
        //    time
    }

    /**
     * Triggered when the clip exits the scene
     */
    public exit(): void { }

    public detach(): this {
        this.track?.remove(this);
        return this;
    }

    public copy(): Clip {
        return Clip.fromJSON(JSON.parse(JSON.stringify(this)));
    }

    /**
     * Modify the properties of the clip and
     * trigger an update afterwards
     */
    public set(props?: Props): this {
        if (props) Object.assign(this, props);

        this.trigger('update', undefined);

        return this;
    }

    public set stop(time: frame | Timestamp) {
        if (typeof time == 'number') {
            this.stop.frames = time;
        } else {
            this._stop = time;
        }

        if (this.stop.millis <= this.start.millis) {
            // make start one frame smaller than the stop
            this.start.millis = this.stop.millis - 1;
        }

        this.trigger('frame', this.stop.frames);
    }

    /**
     * Split the clip into two clips at the specified time
     * @param time split, will use the current frame of the composition
     * a fallback
     * @returns The clip that was created by performing this action
     */
    // public async split(time?: frame | Timestamp): Promise<this> {
    //     if (!time) {
    //         time = this.track?.composition?.frame;
    //     }
    //     if (typeof time == 'number') {
    //         time = Timestamp.fromFrames(time);
    //     }
    //
    //     // invalid cases
    //     if (!time || time.millis <= this.start.millis || time.millis >= this.stop.millis) {
    //         throw new ValidationError({
    //             code: 'splitOutOfRange',
    //             message: 'Cannot split clip at the specified time'
    //         })
    //     }
    //     if (!this.track) {
    //         throw new ValidationError({
    //             code: 'trackNotAttached',
    //             message: 'Track must be attached to a track',
    //         })
    //     }
    //
    //     const newClip = this.copy() as this;
    //
    //
    //
    //     this.stop = time.copy();
    //     copy.start = time.copy().addMillis(1);
    //
    //     // replaceKeyframes(copy, copy.start.subtract(this.start));
    //
    //     const index = this.track.clips.findIndex((c) => c.id == this.id);
    //     await this.track.add(copy, index + 1);
    //
    //     return copy;
    // }

    public async prepareForPlayback(audioCtx: AudioContext, _masterGain: GainNode, mixer?: AudioMixer) {

    }

    public stopAudio() {

    }
}